/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @material-ui core components
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import axios from 'axios';

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDAlert from 'components/MDAlert';

// Settings page components
import Header from '../Header';
import { useUser } from 'context/user';
import SubscriptionModal from './SubscriptionModal';
import { FIREBASE_API_URL } from 'config';

// Types
interface SubscriptionDetails {
  service: 'google' | 'apple' | 'stripe';
  productId?: string;
  startTimeMillis: number;
  expiryTimeMillis: number;
  priceCurrencyCode: string;
  countryCode: string;
  isCancelled: boolean;
  environment?: string;
}

interface SubscriptionResponse {
  subscription: SubscriptionDetails;
  isActive: boolean;
  daysRemaining: number;
}

function SubscriptionInfo(): JSX.Element {
  const { accessToken } = useUser();
  const [subscriptionData, setSubscriptionData] =
    useState<SubscriptionResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const fetchSubscriptionInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!accessToken) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${FIREBASE_API_URL}/api/subscription`, {
        params: { accessToken: accessToken },
      });

      setSubscriptionData(response.data);
    } catch (err) {
      console.log({ err });
      console.error('Error fetching subscription info:', err);
      setError('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionInfo();
  }, [accessToken]);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSubscriptionSuccess = () => {
    // Refresh subscription data after successful update
    fetchSubscriptionInfo();
  };

  return (
    <Card id="subscription-info" sx={{ overflow: 'visible' }}>
      <Header />
      <MDBox p={3}>
        <MDTypography variant="h5">Subscription Information</MDTypography>
      </MDBox>

      {error && (
        <MDBox px={3} mb={2}>
          <MDAlert color="error">{error}</MDAlert>
        </MDBox>
      )}

      <MDBox component="form" pb={3} px={3}>
        {loading ? (
          <MDTypography variant="body2">
            Loading subscription details...
          </MDTypography>
        ) : subscriptionData ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Status
                </MDTypography>
                <MDTypography
                  variant="body2"
                  color={subscriptionData.isActive ? 'success' : 'error'}
                >
                  {subscriptionData.isActive ? 'Active' : 'Inactive'}
                </MDTypography>
              </MDBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Service Provider
                </MDTypography>
                <MDTypography variant="body2" textTransform="capitalize">
                  {subscriptionData.subscription.service}
                </MDTypography>
              </MDBox>
            </Grid>

            {subscriptionData.isActive && (
              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDTypography variant="button" fontWeight="bold">
                    Days Remaining
                  </MDTypography>
                  <MDTypography variant="body2">
                    {subscriptionData.daysRemaining}
                  </MDTypography>
                </MDBox>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Start Date
                </MDTypography>
                <MDTypography variant="body2">
                  {formatDate(subscriptionData.subscription.startTimeMillis)}
                </MDTypography>
              </MDBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Expiry Date
                </MDTypography>
                <MDTypography variant="body2">
                  {formatDate(subscriptionData.subscription.expiryTimeMillis)}
                </MDTypography>
              </MDBox>
            </Grid>

            {subscriptionData.subscription.productId && (
              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDTypography variant="button" fontWeight="bold">
                    Product ID
                  </MDTypography>
                  <MDTypography variant="body2">
                    {subscriptionData.subscription.productId}
                  </MDTypography>
                </MDBox>
              </Grid>
            )}

            {subscriptionData.subscription.isCancelled && (
              <Grid item xs={12}>
                <MDAlert color="warning">
                  This subscription has been cancelled.
                </MDAlert>
              </Grid>
            )}
          </Grid>
        ) : (
          <MDTypography variant="body2">
            No subscription information found.
          </MDTypography>
        )}

        <MDBox mt={3} />

        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
        >
          <MDBox ml="auto">
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              onClick={handleOpenModal}
              disabled={loading}
            >
              Manage Subscription
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>

      {/* Subscription Modal */}
      <SubscriptionModal
        open={isModalOpen}
        onClose={handleCloseModal}
        isActive={subscriptionData?.isActive || false}
        onSuccess={handleSubscriptionSuccess}
      />
    </Card>
  );
}

export default SubscriptionInfo;
